import React, { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

const DashboardLayout = lazy(() => import('./layouts/dashboard'));
const SimpleLayout = lazy(() => import('./layouts/simple'));

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const UserPage = lazy(() => import('./pages/UserPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Page404 = lazy(() => import('./pages/Page404'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const DashboardAppPage = lazy(() => import('./pages/DashboardAppPage'));
const NewGamePage = lazy(() => import('./pages/NewGamePage'));
const GameQuestionsPage = lazy(() => import('./pages/GameQuestionsPage'));
const GameUsersPage = lazy(() => import('./pages/GameUsersPage'));
const GameUserCardPage = lazy(() => import('./pages/GameUserCardPage'));
const PickemEditPage = lazy(() => import('./pages/PickemEditPage'));
const GameQuestionCreatePage = lazy(() => import('./pages/GameQuestionCreatePage'));
const PickemQuestionPage = lazy(() => import('./pages/PickemQuestionPage'));
const ImageLibraryPage = lazy(() => import('./pages/ImageLibraryPage'));
const BannersPage = lazy(() => import('./pages/BannersPage'));
const LeaderboardOverallPage = lazy(() => import('./pages/LeaderboardOverallPage'));
const LeaderboardWeeklyPage = lazy(() => import('./pages/LeaderboardWeeklyPage'));
const LeaderboardsPage = lazy(() => import('./pages/LeaderboardsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const UserSelectionsPage = lazy(() => import('./pages/UserSelectionsPage'));
const LeaguesPage = lazy(() => import('./pages/LeaguesPage'));
const LeagueTournamentsPage = lazy(() => import('./pages/LeagueTournamentsPage'));
const PlayersStatsPage = lazy(() => import('./pages/PlayersStatsPage'));
const FriendsLeaguePage = lazy(() => import('./pages/FriendsLeaguePage'));
const CataloguePage = lazy(() => import('./pages/CataloguePage'));
const FixturesGoaliePage = lazy(() => import('./pages/FixturesGoaliePage'));
const PagesList = lazy(() => import('./pages/PagesList'));
const PageEditPage = lazy(() => import('./pages/PageEditPage'));
const HeadshotsFoldersPage = lazy(() => import('./pages/HeadshotsFoldersPage'));
const HeadshotsPage = lazy(() => import('./pages/HeadshotsPage'));
const LeaguesManagement = lazy(() => import('./pages/LeaguesManagement'));
const LeagueLeaderboard = lazy(() => import('./pages/LeagueLeaderboard'));

const Loading = () => <div>Loading...</div>;

function withSuspense(children) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

function ProtectedRoute({ element }) {
  const isAuthenticated = true;
  return isAuthenticated ? element : <Navigate to="/login" />;
}

export default function Router() {
  const routes = useRoutes([
    {
      path: '/',
      element: <ProtectedRoute element={withSuspense(<DashboardLayout />)} />,
      children: [
        {
          path: 'dashboard',
          element: withSuspense(<DashboardPage />),
          index: true,
        },
        {
          path: 'pickems',
          element: withSuspense(<GamePage />),
        },
        {
          path: 'leagues',
          children: [
            {
              index: true,
              element: withSuspense(<LeaguesManagement />),
            },
            {
              path: ':leagueId/leaderboard',
              element: withSuspense(<LeagueLeaderboard />),
            },
          ],
        },
        {
          path: 'pages',
          children: [
            {
              index: true,
              element: withSuspense(<PagesList />),
            },
            {
              path: 'edit',
              element: withSuspense(<PageEditPage />),
            },
          ],
        },
        {
          path: 'image-library',
          element: withSuspense(<ImageLibraryPage />),
        },
        {
          path: 'banners',
          element: withSuspense(<BannersPage />),
        },
        {
          path: 'leaderboards',
          children: [
            {
              index: true,
              element: withSuspense(<LeaderboardsPage />),
            },
            {
              path: 'overall',
              element: withSuspense(<LeaderboardOverallPage />),
            },
            {
              path: 'weekly',
              element: withSuspense(<LeaderboardWeeklyPage />),
            },
            {
              path: 'view',
              element: withSuspense(<LeaderboardPage />),
            },
          ],
        },
        {
          path: 'user-selections',
          element: withSuspense(<UserSelectionsPage />),
        },
        {
          path: 'leagues-standings',
          element: withSuspense(<LeaguesPage />),
        },
        {
          path: 'friends-leagues',
          element: withSuspense(<FriendsLeaguePage />),
        },
        {
          path: 'catalogue',
          element: withSuspense(<CataloguePage />),
        },
        {
          path: 'league-tournaments',
          element: withSuspense(<LeagueTournamentsPage />),
        },
        {
          path: 'new-game',
          element: withSuspense(<NewGamePage />),
        },
        {
          path: 'pickem',
          children: [
            {
              path: 'edit',
              element: withSuspense(<PickemEditPage />),
            },
            {
              path: 'question',
              element: withSuspense(<PickemQuestionPage />),
            },
          ],
        },
        {
          path: 'stats',
          element: withSuspense(<PlayersStatsPage />),
        },
        {
          path: 'fixtures-squad',
          element: withSuspense(<FixturesGoaliePage />),
        },
        {
          path: 'game',
          children: [
            {
              path: 'questions',
              element: withSuspense(<GameQuestionsPage />),
            },
            {
              path: 'questions/create',
              element: withSuspense(<GameQuestionCreatePage />),
            },
            {
              path: 'users',
              element: withSuspense(<GameUsersPage />),
            },
            {
              path: 'user-card',
              element: withSuspense(<GameUserCardPage />),
            },
          ],
        },
        {
          path: 'app',
          element: withSuspense(<DashboardAppPage />),
        },
        {
          path: 'user',
          element: withSuspense(<UserPage />),
        },
        {
          path: 'products',
          element: withSuspense(<ProductsPage />),
        },
        {
          path: 'blog',
          element: withSuspense(<BlogPage />),
        },
        {
          path: 'headshots',
          children: [
            {
              index: true,
              element: withSuspense(<HeadshotsFoldersPage />),
            },
            {
              path: ':id',
              element: withSuspense(<HeadshotsPage />),
            },
          ],
        },
      ],
    },
    {
      path: '/login',
      element: withSuspense(<LoginPage />),
    },
    {
      element: withSuspense(<SimpleLayout />),
      children: [
        { index: true, element: <Navigate to="/pickems" /> },
        {
          path: '404',
          element: withSuspense(<Page404 />),
        },
        {
          path: '*',
          element: <Navigate to="/404" />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
